// The office's queue: applications that need a look. Compliance first (over a CONFIRMED
// label rate), then peer outliers, then data gaps (no amount, can't convert, size unknown).
import React, { useCallback, useEffect, useState } from "react";
import { decideFlag, errorText, FLAG_WORDS, listFlags, qty } from "./useChemicalsApi";
import "./Chemicals.css";

const SEV = { compliance: "danger", warning: "warn", info: "" };

function detailText(f) {
  const d = f.detail || {};
  const a = f.application || {};
  switch (f.kind) {
    case "over_label_rate":
      return `Applied ${d.applied_per_1000} ${d.unit} per 1,000 sq ft; label max is ${d.label_max_per_1000}.`;
    case "over_annual_max":
      return `${d.applications} applications this year total ${d.year_total_per_1000} per 1,000 sq ft; label yearly max is ${d.label_annual_max}.`;
    case "peer_high":
    case "peer_low":
      return `${d.per_1000} per 1,000 sq ft vs a median of ${d.peer_median} across ${d.peers} applications of this product on this service.`;
    case "unconverted":
      return `Logged in "${d.unit || "no unit"}"; the product is stocked in ${d.product_unit} and the record has no mix rate to convert with.`;
    case "no_quantity":
      return "The tech logged the product but no amount.";
    case "odd_unit":
      return `Unit "${d.unit || "blank"}", amount ${d.qty}.`;
    case "not_in_catalog":
      return `FieldRoutes chemical ${d.fr_chemical_id} isn't in the product catalog.`;
    case "size_unknown":
      return `No square footage on the record or the property, so the rate per 1,000 sq ft can't be checked. Applied ${qty(a.concentrate_qty, a.concentrate_unit)}.`;
    default:
      return "";
  }
}

export default function ChemicalFlagsPage() {
  const [data, setData] = useState(null);
  const [severity, setSeverity] = useState("");
  const [kind, setKind] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setData(await listFlags({ severity: severity || undefined, kind: kind || undefined }));
      setError("");
    } catch (err) {
      setError(errorText(err, "Couldn't load the flags."));
    }
  }, [severity, kind]);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, action) => {
    try { await decideFlag(id, action); load(); } catch (err) { setError(errorText(err, "Couldn't save that.")); }
  };

  const summary = data?.summary || {};
  return (
    <div className="chem-page">
      <div className="chem-head">
        <div>
          <h1>Application flags</h1>
          <div className="chem-sub">Applications from FieldRoutes that need an office look. Nothing here changes FieldRoutes.</div>
        </div>
        <div className="chem-seg">
          {[["", "All"], ["compliance", "Compliance"], ["warning", "Warnings"], ["info", "Data gaps"]].map(([k, l]) => (
            <button key={k || "all"} type="button" className={severity === k ? "is-active" : ""} onClick={() => setSeverity(k)}>{l}</button>
          ))}
        </div>
      </div>
      <div className="chem-pills">
        {Object.entries(FLAG_WORDS).filter(([k]) => summary[k]).map(([k, l]) => (
          <button key={k} type="button" className={`chem-pill ${kind === k ? "chem-pill--warn" : ""}`} style={{ border: "none", cursor: "pointer" }}
                  onClick={() => setKind(kind === k ? "" : k)}>{l}: {summary[k]}</button>
        ))}
      </div>
      {error ? <div className="chem-msg chem-msg--err">{error}</div> : null}
      <section className="chem-card">
        {!data ? <div className="chem-empty">Loading…</div> : data.results.length === 0 ? (
          <div className="chem-empty">No open flags for this filter.</div>
        ) : (
          <div className="chem-table-wrap">
            <table className="chem-table">
              <thead><tr><th>Flag</th><th>Customer</th><th>Applied</th><th>Product</th><th>Detail</th><th /></tr></thead>
              <tbody>{data.results.map((f) => (
                <tr key={f.id}>
                  <td><span className={`chem-pill ${SEV[f.severity] ? `chem-pill--${SEV[f.severity]}` : ""}`}>{FLAG_WORDS[f.kind] || f.kind_label}</span></td>
                  <td>{f.customer || "Not linked"}</td>
                  <td>{f.application.applied_date || "—"}<div className="chem-detail">{[f.application.service_type, f.application.tech].filter(Boolean).join(" · ")}</div></td>
                  <td>{f.application.product || "—"}<div className="chem-detail">{f.application.qty != null ? `${qty(f.application.qty, f.application.unit)} logged` : ""}</div></td>
                  <td className="chem-detail">{detailText(f)}</td>
                  <td><span className="chem-pills">
                    <button type="button" className="chem-btn" onClick={() => decide(f.id, "resolve")}>Resolved</button>
                    <button type="button" className="chem-btn" onClick={() => decide(f.id, "dismiss")}>Dismiss</button>
                  </span></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
